/**
 * Dashboard service — new endpoints for the professional dashboard.
 * Uses browserFetch directly (no orval codegen required for these new endpoints).
 */
import { browserFetch } from '@website/lib/api/browserFetch';

// ── Response types (mirror domain schemas) ───────────────────────────────────

export interface DashboardOverviewBooking {
  id: string;
  bookingId: string;
  startsAt: string;
  endsAt: string;
  activityTitle: string;
  menteeName: string;
  status: 'live' | 'upcoming' | 'completed';
}

export interface DashboardOverviewUpNext {
  id: string;
  activityTitle: string;
  activityImageKey?: string | null;
  menteeName: string;
  menteeEmail?: string;
  menteePhone?: string;
  menteeAvatarKey?: string | null;
  startsAt: string;
  endsAt: string;
  totalPrice: number;
  currency: string;
  paymentStatus: string;
  notes?: string;
  isLive: boolean;
  location:
    | { type: 'home'; addressLine: string; city: string }
    | { type: 'venue'; label: string; city?: string }
    | { type: 'online' };
  groupSize?: { current: number; total: number };
}

export interface DashboardOverviewResponse {
  thisMonthEarnings: { amount: number; deltaPercent: number };
  nextPayout: { amount: number; date: string | null } | null;
  cardPaymentsEnabled: boolean;
  newBookingsThisMonth: { count: number; deltaPercent: number };
  newMenteesThisMonth: { count: number; deltaPercent: number };
  upNext: DashboardOverviewUpNext | null;
  todayBookings: DashboardOverviewBooking[];
  thisWeekBookings: DashboardOverviewBooking[];
}

export interface DashboardCashflowDay {
  date: string;
  cash: number;
  online: number;
  booked: number;
  bookingsCount: number;
}

export interface DashboardCashflowResponse {
  month: string;
  todayIso: string;
  days: DashboardCashflowDay[];
  totals: { cash: number; online: number; booked: number; total: number };
  deltaVsLastMonthSameDay: { amount: number; percent: number };
}

export interface DashboardBookingsChartDay {
  date: string;
  newCount: number;
  completedCount: number;
  cancelledSameMonth: number;
  cancelledPrevMonth: number;
  cancelledCount: number;
}

export interface DashboardBookingsChartResponse {
  month: string;
  todayIso: string;
  days: DashboardBookingsChartDay[];
  totals: {
    newCount: number;
    netNewCount: number;
    completedCount: number;
    cancelledCount: number;
    cancellationRatePercent: number;
  };
}

export interface DashboardMenteeItem {
  clientId: string;
  name: string;
  avatarKey?: string | null;
  bookingCount: number;
  lastBookingDate?: string;
  firstBookingDate?: string;
}

export interface DashboardMenteesResponse {
  topMentees: { last3m: DashboardMenteeItem[]; last6m: DashboardMenteeItem[] };
  newMentees: { last3m: DashboardMenteeItem[]; last6m: DashboardMenteeItem[] };
}

// ── Fetch functions ───────────────────────────────────────────────────────────

export async function getDashboardOverview(locale?: string): Promise<DashboardOverviewResponse> {
  const qs = locale ? `?locale=${encodeURIComponent(locale)}` : '';
  return browserFetch<DashboardOverviewResponse>(`/dashboard/overview${qs}`, { method: 'GET' });
}

export async function getDashboardCashflow(month: string): Promise<DashboardCashflowResponse> {
  return browserFetch<DashboardCashflowResponse>(`/dashboard/cashflow?month=${encodeURIComponent(month)}`, {
    method: 'GET'
  });
}

export async function getDashboardBookingsChart(month: string): Promise<DashboardBookingsChartResponse> {
  return browserFetch<DashboardBookingsChartResponse>(`/dashboard/bookings-chart?month=${encodeURIComponent(month)}`, {
    method: 'GET'
  });
}

export async function getDashboardMentees(): Promise<DashboardMenteesResponse> {
  return browserFetch<DashboardMenteesResponse>('/dashboard/mentees', { method: 'GET' });
}
